'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { useLabsSurface } from '../labs-surface';
import { useLabsStore, usePack } from '../pack-context';
import { PillTag, StepNav } from './_components';
import { StepShell } from './step-shell';

/**
 * M1 · Intro — the framing slide for the swipe deck, rendered through the fs-* design system
 * (fs-* tokens + DM Serif Display / Inter).
 *
 * Layout: centered framing slide — eyebrow pill, serif headline, a muted lead paragraph, then an
 * ACCENT paragraph (darker ink, medium weight, closing clause emphasised) that sets it apart from
 * the lead copy. Back is inherited from StepNav (top-left of the paired footer); the screen's own
 * primary "Continue" is the right half of the same pair, so Back is never doubled.
 *
 * Token mapping: lead body #3A4252 → `fs-text-subtle`; accent body #0A0E1A (medium weight) →
 * `fs-text` + font-medium; the emphasised clause → italic.
 *
 * `accentPre` carries a `{count}` placeholder — the deck length is a property of the PACK's card
 * list, so the sentence ("You'll see 8 real activities…") is interpolated here rather than being
 * frozen into the copy string, which would silently lie for a pack with a different deck.
 */
export function M1Intro() {
  const $ = _c(24);
  const {
    copy,
    m1
  } = usePack();
  const next = useLabsStore(_temp);
  const {
    moduleCount
  } = useLabsSurface();
  let t0;
  if ($[0] !== copy.m1.intro.accentPre || $[1] !== m1.cards.length) {
    t0 = copy.m1.intro.accentPre.replace("{count}", String(m1.cards.length));
    $[0] = copy.m1.intro.accentPre;
    $[1] = m1.cards.length;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const accentPre = t0;
  let t1;
  if ($[3] !== moduleCount) {
    t1 = <PillTag>Module 1 of {moduleCount}</PillTag>;
    $[3] = moduleCount;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== copy.m1.intro.headline) {
    t2 = <h1 className="font-instrument text-fs-display-sm text-fs-text sm:text-fs-display-md">{copy.m1.intro.headline}</h1>;
    $[5] = copy.m1.intro.headline;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== copy.m1.intro.lead) {
    t3 = <p className="font-medium text-fs-text-subtle text-fs-title-lg">{copy.m1.intro.lead}</p>;
    $[7] = copy.m1.intro.lead;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== copy.m1.intro.accentEm) {
    t4 = <em className="italic">{copy.m1.intro.accentEm}</em>;
    $[9] = copy.m1.intro.accentEm;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== accentPre || $[12] !== t4) {
    t5 = <p className="font-medium text-fs-text text-fs-title-lg">{accentPre} {t4}</p>;
    $[11] = accentPre;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t1 || $[15] !== t2 || $[16] !== t3 || $[17] !== t5) {
    t6 = <div className="flex w-full max-w-[560px] flex-col items-center gap-4 text-center">{t1}{t2}{t3}{t5}</div>;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== next) {
    t7 = <StepNav onNext={next} />;
    $[19] = next;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t6 || $[22] !== t7) {
    t8 = <StepShell><div className="flex flex-col items-center gap-8">{t6}{t7}</div></StepShell>;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}
function _temp(s) {
  return s.next;
}